import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from "lucide-react";
import { cn } from "@/lib/utils";

const DEFAULT_LIMIT_OPTIONS = [10, 25, 50] as const;
const PAGE_WINDOW = 5;

interface ListPaginationProps {
  page: number;
  limit: number;
  total: number;
  loading?: boolean;
  limitOptions?: readonly number[];
  onPageChange: (page: number) => void;
  onLimitChange: (limit: number) => void;
  className?: string;
}

function visiblePages(page: number, totalPages: number): number[] {
  if (totalPages <= PAGE_WINDOW) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }
  const half = Math.floor(PAGE_WINDOW / 2);
  let start = Math.max(1, page - half);
  const end = Math.min(totalPages, start + PAGE_WINDOW - 1);
  start = Math.max(1, end - PAGE_WINDOW + 1);
  return Array.from({ length: end - start + 1 }, (_, i) => start + i);
}

export function ListPagination({
  page,
  limit,
  total,
  loading = false,
  limitOptions = DEFAULT_LIMIT_OPTIONS,
  onPageChange,
  onLimitChange,
  className,
}: ListPaginationProps) {
  if (total <= 0) {
    return null;
  }

  const totalPages = Math.max(1, Math.ceil(total / limit));
  const from = (page - 1) * limit + 1;
  const to = Math.min(page * limit, total);
  const pages = visiblePages(page, totalPages);
  const showFirstPrev = page > 1;
  const showNextLast = page < totalPages;

  const segBtn =
    "inline-flex h-8 min-w-8 items-center justify-center border-r border-border px-2.5 text-xs font-medium transition-colors last:border-r-0 hover:bg-muted disabled:pointer-events-none disabled:opacity-40";

  const activePageBtn =
    "pointer-events-none bg-[#9333F5] text-primary-foreground hover:bg-[#9333F5]";

  return (
    <div className={cn("flex flex-wrap items-center justify-between gap-3 pt-4", className)}>
      <p className="flex h-8 items-center text-xs text-muted-foreground">
        Showing{" "}
        <span className="font-medium text-foreground">{from}</span>
        {" to "}
        <span className="font-medium text-foreground">{to}</span>
        {" of "}
        <span className="font-medium text-foreground">{total}</span>
        {" results"}
      </p>

      <div className="flex flex-wrap items-center gap-2">
        <div className="flex h-8 items-stretch overflow-hidden rounded-md border border-border bg-background text-xs">
          <span className="flex items-center px-2.5 text-muted-foreground">Per page</span>
          <div className="relative flex items-center border-l border-border">
            <select
              value={limit}
              disabled={loading}
              onChange={(event) => onLimitChange(Number(event.target.value))}
              className="h-full appearance-none bg-transparent py-0 pl-2.5 pr-6 text-xs font-medium text-foreground outline-none disabled:opacity-50"
              aria-label="Rows per page"
            >
              {limitOptions.map((option) => (
                <option key={option} value={option} className="bg-popover text-popover-foreground">
                  {option}
                </option>
              ))}
            </select>
            <span className="pointer-events-none absolute right-1.5 top-1/2 -translate-y-1/2 text-[10px] text-muted-foreground">
              ▾
            </span>
          </div>
        </div>

        <div className="flex h-8 items-stretch overflow-hidden rounded-md border border-border bg-background">
          {showFirstPrev ? (
            <>
              <button
                type="button"
                className={segBtn}
                disabled={loading}
                onClick={() => onPageChange(1)}
                aria-label="First page"
                title="First page"
              >
                <ChevronsLeft className="h-3.5 w-3.5" />
              </button>
              <button
                type="button"
                className={segBtn}
                disabled={loading}
                onClick={() => onPageChange(page - 1)}
                aria-label="Previous page"
                title="Previous page"
              >
                <ChevronLeft className="h-3.5 w-3.5" />
              </button>
            </>
          ) : null}

          {pages.map((pageNumber) => {
            const isActive = pageNumber === page;
            return (
            <button
              key={pageNumber}
              type="button"
              className={`${segBtn} ${isActive ? activePageBtn : "text-foreground"}`}
              disabled={loading}
              onClick={() => onPageChange(pageNumber)}
              aria-label={`Page ${pageNumber}`}
              aria-current={isActive ? "page" : undefined}
            >
              {pageNumber}
            </button>
            );
          })}

          {showNextLast ? (
            <>
              <button
                type="button"
                className={segBtn}
                disabled={loading}
                onClick={() => onPageChange(page + 1)}
                aria-label="Next page"
                title="Next page"
              >
                <ChevronRight className="h-3.5 w-3.5" />
              </button>
              <button
                type="button"
                className={segBtn}
                disabled={loading}
                onClick={() => onPageChange(totalPages)}
                aria-label="Last page"
                title="Last page"
              >
                <ChevronsRight className="h-3.5 w-3.5" />
              </button>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}
